import type { KeyboardEvent } from 'react';
import { EmptySlot } from './empty-slot';
import PitchPlayer from '@components/pitch-player';
import type {
  PitchPlayerFixture,
  PitchPlayerLiveEvents
} from '@components/pitch-player';
import type { PlayerPriceChange } from '@api-service/players';
import type { PlayerStatus } from '@lib/my-team/player-availability';

const POSITION_ORDER = ['GK', 'DEF', 'MID', 'FWD'] as const;

export type LineupPlayer = {
  id?: string;
  displayName: string;
  position: 'GK' | 'DEF' | 'MID' | 'FWD';
  team: { shortName: string };
  /** CDN-hosted team kit image from the ffh-backend API, when available. */
  kitImageUrl?: string;
  price: number;
  fixtures: PitchPlayerFixture[];
  isCaptain?: boolean;
  isViceCaptain?: boolean;
  /** Live-mode points; when set, the tile swaps price for points and hides fixture tiles. */
  livePoints?: number;
  liveEvents?: PitchPlayerLiveEvents;
  /** Picks with no fixture this gameweek render faded. */
  isFaded?: boolean;
  /**
   * The pick's points come from a simulated fixture, so its figures and event
   * icons are fabricated. Marks the tile.
   */
  isSimulated?: boolean;
  /** Predicted price change for the price-changes view. */
  priceChange?: PlayerPriceChange;
  /** FPL availability status; drives the injury/warning treatment. */
  status?: PlayerStatus;
  chanceOfPlaying?: number | null;
};

export type TeamLineupProps = {
  starting: LineupPlayer[];
  bench?: LineupPlayer[];
  maxFixtures?: number;
  onPlayerPress?: (playerId: string) => void;
  selectedPlayerId?: string | null;
  disabledPlayerIds?: string[];
  incomingPlayerIds?: string[];
  outgoingPlayerIds?: string[];
  forceShowQuickActions?: boolean;
  onMakeCaptain?: (playerId: string) => void;
  onMakeViceCaptain?: (playerId: string) => void;
  onTransferOut?: (playerId: string) => void;
  onCancelTransferOut?: (playerId: string) => void;
  onEmptySlotPress?: () => void;
  /** Hides predicted-points figures on every tile (non-paying members). */
  hidePredictedPoints?: boolean;
  /**
   * Per-gameweek prediction lock for non-paying members: fixture tiles whose
   * gameweek this returns true for show a padlock instead of the points.
   */
  getIsFixturePredictionLocked?: (gameweek: number | undefined) => boolean;
  /** Swaps the fixture grid on every tile for the predicted price change. */
  isPriceChangeView?: boolean;
  /** Shows a loading state on the price change tiles while fetching. */
  isPriceChangeLoading?: boolean;
};

const groupByPosition = (players: LineupPlayer[]) => ({
  GK: players.filter((player) => player.position === 'GK'),
  DEF: players.filter((player) => player.position === 'DEF'),
  MID: players.filter((player) => player.position === 'MID'),
  FWD: players.filter((player) => player.position === 'FWD')
});

type InteractiveSlotProps = {
  player: LineupPlayer;
  maxFixtures?: number;
  isSelected: boolean;
  isDisabled: boolean;
  isIncoming: boolean;
  isOutgoing: boolean;
  forceShowQuickActions?: boolean;
  hidePredictedPoints?: boolean;
  getIsFixturePredictionLocked?: (gameweek: number | undefined) => boolean;
  isPriceChangeView?: boolean;
  isPriceChangeLoading?: boolean;
  onPlayerPress?: (playerId: string) => void;
  onMakeCaptain?: () => void;
  onMakeViceCaptain?: () => void;
  onTransferOut?: () => void;
  onCancelTransferOut?: () => void;
};

const InteractiveSlot = ({
  player,
  maxFixtures,
  isSelected,
  isDisabled,
  isIncoming,
  isOutgoing,
  forceShowQuickActions = false,
  hidePredictedPoints = false,
  getIsFixturePredictionLocked,
  isPriceChangeView = false,
  isPriceChangeLoading = false,
  onPlayerPress,
  onMakeCaptain,
  onMakeViceCaptain,
  onTransferOut,
  onCancelTransferOut
}: InteractiveSlotProps) => {
  const isPressable = Boolean(onPlayerPress && player.id && !isDisabled);

  const handlePress = () => {
    if (isPressable && player.id) onPlayerPress?.(player.id);
  };

  const slotClass = [
    'relative w-full transition-all focus:outline-none',
    isSelected ? 'ring-2 ring-primary rounded-lg' : '',
    isDisabled ? 'opacity-40' : '',
    isPressable
      ? 'cursor-pointer hover:scale-[1.02] focus-visible:ring-2 focus-visible:ring-primary rounded-lg'
      : ''
  ]
    .filter(Boolean)
    .join(' ');

  const handleKeyDown = (event: KeyboardEvent<HTMLDivElement>) => {
    if (!isPressable) return;
    if (event.key === 'Enter' || event.key === ' ') {
      event.preventDefault();
      handlePress();
    }
  };

  return (
    <div
      className={slotClass}
      onClick={isPressable ? handlePress : undefined}
      onKeyDown={handleKeyDown}
      role={isPressable ? 'button' : undefined}
      tabIndex={isPressable ? 0 : undefined}
      aria-disabled={!isPressable}
      data-cy="lineup-player-slot"
      data-cy-player-id={player.id ?? ''}
    >
      <PitchPlayer
        name={player.displayName}
        teamShortName={player.team.shortName}
        kitImageUrl={player.kitImageUrl}
        price={player.price}
        fixtures={player.fixtures}
        maxFixtures={maxFixtures}
        hidePredictedPoints={hidePredictedPoints}
        getIsFixturePredictionLocked={getIsFixturePredictionLocked}
        isCaptain={player.isCaptain}
        isViceCaptain={player.isViceCaptain}
        isIncoming={isIncoming}
        isOutgoing={isOutgoing}
        forceShowQuickActions={forceShowQuickActions}
        livePoints={player.livePoints}
        liveEvents={player.liveEvents}
        isFaded={player.isFaded}
        isSimulated={player.isSimulated}
        isPriceChangeView={isPriceChangeView}
        priceChange={player.priceChange}
        isPriceChangeLoading={isPriceChangeLoading}
        status={player.status}
        chanceOfPlaying={player.chanceOfPlaying}
        onMakeCaptain={onMakeCaptain}
        onMakeViceCaptain={onMakeViceCaptain}
        onTransferOut={onTransferOut}
        onCancelTransferOut={onCancelTransferOut}
      />
    </div>
  );
};

const TeamLineup = ({
  starting,
  bench,
  maxFixtures,
  onPlayerPress,
  selectedPlayerId,
  disabledPlayerIds,
  incomingPlayerIds,
  outgoingPlayerIds,
  forceShowQuickActions,
  onMakeCaptain,
  onMakeViceCaptain,
  onTransferOut,
  onCancelTransferOut,
  onEmptySlotPress,
  hidePredictedPoints,
  getIsFixturePredictionLocked,
  isPriceChangeView,
  isPriceChangeLoading
}: TeamLineupProps) => {
  const groups = groupByPosition(starting);
  const disabledIds = new Set(disabledPlayerIds ?? []);
  const incomingIds = new Set(incomingPlayerIds ?? []);
  const outgoingIds = new Set(outgoingPlayerIds ?? []);

  const slotFor = (player: LineupPlayer) => {
    const id = player.id ?? '';
    const isIncoming = Boolean(id) && incomingIds.has(id);
    const isOutgoing = Boolean(id) && outgoingIds.has(id);

    if (isOutgoing) {
      return (
        <EmptySlot
          position={player.position}
          onPress={onEmptySlotPress}
          onCancel={
            onCancelTransferOut && id
              ? () => onCancelTransferOut(id)
              : undefined
          }
        />
      );
    }

    const bindCaptain =
      onMakeCaptain && id && !isIncoming ? () => onMakeCaptain(id) : undefined;
    const bindViceCaptain =
      onMakeViceCaptain && id && !isIncoming
        ? () => onMakeViceCaptain(id)
        : undefined;
    const bindTransferOut =
      onTransferOut && id && !isIncoming ? () => onTransferOut(id) : undefined;
    const bindCancel =
      onCancelTransferOut && id && isIncoming
        ? () => onCancelTransferOut(id)
        : undefined;
    return (
      <InteractiveSlot
        player={player}
        maxFixtures={maxFixtures}
        isSelected={Boolean(id) && id === selectedPlayerId}
        isDisabled={Boolean(id) && disabledIds.has(id)}
        isIncoming={isIncoming}
        isOutgoing={false}
        forceShowQuickActions={forceShowQuickActions}
        hidePredictedPoints={Boolean(hidePredictedPoints)}
        getIsFixturePredictionLocked={getIsFixturePredictionLocked}
        isPriceChangeView={Boolean(isPriceChangeView)}
        isPriceChangeLoading={Boolean(isPriceChangeLoading)}
        onPlayerPress={onPlayerPress}
        onMakeCaptain={bindCaptain}
        onMakeViceCaptain={bindViceCaptain}
        onTransferOut={bindTransferOut}
        onCancelTransferOut={bindCancel}
      />
    );
  };

  return (
    <div className="space-y-4" data-cy="team-lineup">
      <div
        className="relative w-full max-w-3xl mx-auto bg-no-repeat bg-cover bg-center rounded-lg overflow-hidden py-6 px-1 md:px-2"
        style={{ backgroundImage: "url('/images/pitches/pitch.png')" }}
        data-cy="team-lineup-pitch"
      >
        <div className="space-y-2 md:space-y-4">
          {POSITION_ORDER.map((position) => (
            <div
              key={position}
              className="flex justify-center gap-1 md:gap-3"
              data-cy={`team-lineup-row-${position.toLowerCase()}`}
              data-testid={`team-lineup-row-${position.toLowerCase()}`}
            >
              {groups[position].map((player, idx) => (
                <div
                  key={player.id || `${position}-slot-${idx}`}
                  className="relative flex-1 min-w-0 max-w-[140px]"
                >
                  {slotFor(player)}
                </div>
              ))}
            </div>
          ))}
        </div>
      </div>

      {bench && bench.length > 0 && (
        <div className="m-auto flex max-w-[620px] justify-around py-3 bg-success-100 rounded-lg mt-2 md:mt-3">
          <div
            className="flex justify-around w-full gap-2"
            data-cy="team-lineup-bench"
            data-testid="team-lineup-bench"
          >
            {bench.map((player, idx) => (
              <div
                key={player.id || `bench-slot-${idx}`}
                className="flex-1 min-w-0 max-w-[140px] px-px"
              >
                <div
                  className="flex justify-center text-xs font-bold text-success-800 mb-1"
                  data-cy="team-lineup-bench-position"
                >
                  {player.position}
                </div>
                {slotFor(player)}
              </div>
            ))}
          </div>
        </div>
      )}
    </div>
  );
};

export default TeamLineup;
